import { useCallback, useEffect, useRef } from "react"
import debounce from 'lodash/debounce';

export interface IUseLoadMore {
    checkLoadMore: () => void,
    scrollerRef: (node: HTMLElement) => void;
}

// Listens for scroll and resize events and calls "loadMore()" if needed.
// Callers must attach "scrollerRef" to a scrollable div like:
//     <div ref={scrollerRef}>
export function useLoadMore(
    loadMore: () => void,
    maxDistanceFromBottom?: number,
): IUseLoadMore {
    if (!maxDistanceFromBottom) {
        maxDistanceFromBottom = 200;
    }
    // We're using refs here so that we don't have to rebind events on every render.
    // When "checkLoadMore" runs, it can access the refs to get the current state.
    const loadMoreRef = useRef(loadMore);
    const elRef = useRef<HTMLElement>();
    loadMoreRef.current = loadMore;

    // Checks scroll positioning to see if we need to load more.
    // We're using a callback ref for this and for the debounce, because debounce needs
    // a stable function identity to work. We can't recreate the debounce function every render.
    const checkLoadMore = useCallback(() => {
        const el = elRef.current;
        if (el) {
            // See if we're near the bottom.
            // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
            const distFromBottom = (el.scrollHeight - el.scrollTop) - el.clientHeight;

            if (distFromBottom < maxDistanceFromBottom) {
                loadMoreRef.current();
            }
        }
    }, []);

    const debouncedCheck = useCallback(
        debounce(checkLoadMore, 250, {maxWait: 250}),
        []
    );

    // We're listening for scroll events on the scroller div.
    // The caller must use this callback as a ref, like:
    //     <div ref={scrollerRef}>
    //
    // See: https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
    const scrollerRef = useCallback((node: HTMLElement) => {
        elRef.current = node;

        if (node) {
            node.addEventListener('scroll', debouncedCheck, {passive: true});
            
            return () => {
                node.removeEventListener('scroll', debouncedCheck);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener('resize', debouncedCheck);
        
        return () => {
            window.removeEventListener('resize', debouncedCheck);
        }
    }, []);

    return {
        checkLoadMore: checkLoadMore,
        scrollerRef: scrollerRef,
    };
}